import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  CircularProgress,
  Stack,
} from '@mui/material';
import { getPosReceipt } from '../../services/api';
import { formatPrice } from '../../utils/formatPrice';

const buildCurrency = (restaurant, fallback) =>
  restaurant
    ? { code: restaurant.currency_code, symbol: restaurant.currency_symbol, position: restaurant.currency_position }
    : fallback;

/**
 * Settled-tab receipt with a Print action. Printing opens a minimal standalone
 * window (so the whole app chrome isn't printed) and triggers the browser dialog.
 */
const ReceiptDialog = ({ open, restaurantId, sessionId, currency, onClose }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open || !sessionId) return;
    let active = true;
    setLoading(true);
    getPosReceipt(restaurantId, sessionId)
      .then((res) => { if (active) setData(res.data); })
      .catch(() => { if (active) setData(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [open, restaurantId, sessionId]);

  const curr = buildCurrency(data?.restaurant, currency);
  const lineItems = [];
  (data?.orders || []).forEach((order) => {
    (order.items || []).forEach((it) => {
      lineItems.push({
        name: it.name,
        quantity: it.quantity,
        price: it.price,
        addons: (it.selected_addons || []).map((a) => a.option).filter(Boolean),
      });
    });
  });
  const fin = data?.financials || { subtotal: 0, tax_amount: 0, total: 0, paid: 0 };

  const handlePrint = () => {
    if (!data) return;
    const rows = lineItems
      .map((li) => {
        const addons = li.addons.length ? `<div style="font-size:11px;color:#666">${li.addons.join(', ')}</div>` : '';
        return `<tr><td>${li.quantity}× ${li.name}${addons}</td><td style="text-align:right">${formatPrice(li.price * li.quantity, curr)}</td></tr>`;
      })
      .join('');
    const payments = (data.payments || [])
      .map((p) => `<tr><td>${p.method === 'cash' ? t('pos.payment.cash', 'Cash') : t('pos.payment.card', 'Card')}</td><td style="text-align:right">${formatPrice(p.amount, curr)}</td></tr>`)
      .join('');
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>${t('pos.receipt.title', 'Receipt')}</title>
      <style>body{font-family:-apple-system,Segoe UI,Roboto,sans-serif;max-width:320px;margin:0 auto;padding:16px;color:#111}
      h1{font-size:18px;text-align:center;margin:0 0 4px} .muted{color:#666;text-align:center;font-size:12px;margin:0 0 12px}
      table{width:100%;border-collapse:collapse;font-size:13px} td{padding:3px 0;vertical-align:top}
      .tot td{font-weight:700} hr{border:none;border-top:1px dashed #999;margin:10px 0}</style></head>
      <body>
        <h1>${data.restaurant?.name || ''}</h1>
        <p class="muted">${t('pos.tabs.table', 'Table')} ${data.tab?.table_number || ''}</p>
        <table>${rows}</table>
        <hr/>
        <table>
          <tr><td>${t('pos.receipt.subtotal', 'Subtotal')}</td><td style="text-align:right">${formatPrice(fin.subtotal, curr)}</td></tr>
          <tr><td>${data.restaurant?.tax_label || t('pos.receipt.tax', 'Tax')}</td><td style="text-align:right">${formatPrice(fin.tax_amount, curr)}</td></tr>
          <tr class="tot"><td>${t('pos.receipt.total', 'Total')}</td><td style="text-align:right">${formatPrice(fin.total, curr)}</td></tr>
        </table>
        <hr/>
        <table>${payments}</table>
      </body></html>`;
    const w = window.open('', '_blank', 'width=380,height=640');
    if (!w) return;
    w.document.write(html);
    w.document.close();
    w.focus();
    w.print();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('pos.receipt.title', 'Receipt')}</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={26} /></Box>
        ) : !data ? (
          <Typography sx={{ color: 'text.secondary', py: 2 }}>{t('pos.receipt.loadFailed', 'Could not load receipt.')}</Typography>
        ) : (
          <Box>
            <Typography sx={{ textAlign: 'center', fontWeight: 700, fontSize: 18 }}>{data.restaurant?.name}</Typography>
            <Typography sx={{ textAlign: 'center', color: 'text.secondary', fontSize: 13, mb: 2 }}>
              {t('pos.tabs.table', 'Table')} {data.tab?.table_number}
            </Typography>
            {lineItems.map((li, idx) => (
              <Stack key={idx} direction="row" justifyContent="space-between" sx={{ py: 0.5 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 14 }}>{li.quantity}× {li.name}</Typography>
                  {li.addons.length > 0 && <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{li.addons.join(', ')}</Typography>}
                </Box>
                <Typography sx={{ fontSize: 14 }}>{formatPrice(li.price * li.quantity, curr)}</Typography>
              </Stack>
            ))}
            <Divider sx={{ my: 1.5 }} />
            <Stack direction="row" justifyContent="space-between"><Typography color="text.secondary">{t('pos.receipt.subtotal', 'Subtotal')}</Typography><Typography>{formatPrice(fin.subtotal, curr)}</Typography></Stack>
            <Stack direction="row" justifyContent="space-between"><Typography color="text.secondary">{data.restaurant?.tax_label || t('pos.receipt.tax', 'Tax')}</Typography><Typography>{formatPrice(fin.tax_amount, curr)}</Typography></Stack>
            <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}><Typography sx={{ fontWeight: 800 }}>{t('pos.receipt.total', 'Total')}</Typography><Typography sx={{ fontWeight: 800 }}>{formatPrice(fin.total, curr)}</Typography></Stack>
            {(data.payments || []).length > 0 && (
              <>
                <Divider sx={{ my: 1.5 }} />
                {data.payments.map((p) => (
                  <Stack key={p.id} direction="row" justifyContent="space-between">
                    <Typography color="text.secondary">{p.method === 'cash' ? t('pos.payment.cash', 'Cash') : t('pos.payment.card', 'Card')}</Typography>
                    <Typography>{formatPrice(p.amount, curr)}</Typography>
                  </Stack>
                ))}
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">{t('common.close', 'Close')}</Button>
        <Button variant="contained" onClick={handlePrint} disabled={!data}>{t('pos.receipt.print', 'Print')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReceiptDialog;
